<template>
  <div class="wrapper-mc-timeline-day" ref="wrapper-mc-timeline-day">
    <table class="mc-timeline-day">
      <tr>
        <th class="resource"><span class="artificial-border"></span>{{ resourceField.label }}</th>
        <th v-for="timeLabel in timeLabels">{{ timeLabel }}</th>
      </tr>

      <template v-if="resources.length == 0">
        <tr class="position-relative">
          <td class="resource">
            <div class="resource__container">
              <div class="__title">{{ $t('fleetAvailability.others.noData') }}</div>
            </div>
          </td>

          <td class="time-slot" v-for="_ in timeLabels"></td>
        </tr>
      </template>

      <template v-else>
        <tr v-for="resource in resources" class="position-relative">
          <td class="resource" v-if="resource.isShown" :rowSpan="resource.rowSpan">
            <div class="resource__container">
              <div
                v-if="!$slots['resource-item'] && !$scopedSlots['resource-item']"
                class="__title"
              >
                {{ resource.resourceValue }}
              </div>

              <i v-if="resource.rowSpan > 1" class="icon-fill_alert-triangle" />
            </div>
          </td>

          <td class="time-slot" v-for="(_, idx) in timeLabels">
            <template v-for="event in resource.events">
              <template v-if="idx == 0">
                <EventItemHourly
                  :startTime="event.start"
                  :endTime="event.end"
                  :color="colorMappings[event.status.name]"
                  :title="event.number"
                  :contents="event.rows"
                  :key="`event-item-${event.id}`"
                  :id="`popover-target-${event.id}`"
                  @click="() => handleClickEvent(event)"
                  @hover="(e) => handleHoverEvent(e, event)"
                />

                <b-popover
                  no-fade
                  custom-class="popover-event-item"
                  :key="`popover-${event.id}`"
                  :target="`popover-target-${event.id}`"
                  :ref="`popover-${event.id}-ref`"
                >
                  <!-- TODO: title can be applied by global event -->
                  <template #title>{{ event.number }}</template>
                  <template #default>
                    <div class="popover-row" v-for="content in event.rows" v-html="content"></div>
                  </template>
                </b-popover>
              </template>
            </template>
          </td>
        </tr>
      </template>

      <div class="current-time" ref="current-time"></div>
    </table>
  </div>
</template>

<script>
import EventItemHourly from './EventItemHourly.vue'
import moment from 'moment'

import get from 'lodash/get'
import { fleetDestination } from '../lib/constants/fleet-destination'

export default {
  components: {
    EventItemHourly,
  },
  inject: ['timeSelected'],
  props: {
    resources: {
      type: Array,
    },
    resourceField: {
      type: Object,
    },
    colorMappings: {
      type: Object,
    },
  },
  data() {
    return {
      updatePointerInterval: null,
    }
  },
  computed: {
    timeLabels() {
      return [...new Array(24)].map((_, idx) => `${String(idx).padStart(2, '0')}:00`)
    },
  },
  watch: {
    timeSelected() {
      this.setCurrentTimePointer()
    },
    resourceField() {
      this.scrollToCurrentTime()
    },
  },
  mounted() {
    this.setCurrentTimePointer()
  },
  beforeDestroy() {
    clearInterval(this.updatePointerInterval)
  },
  methods: {
    getResourceValue(resource, key) {
      return get(resource, key)
    },
    scrollToCurrentTime() {
      const scrollContainer = this.$refs['wrapper-mc-timeline-day']
      const currentTimePointer = this.$refs['current-time']

      // Calculate the target scroll position to center the content
      const containerWidth = scrollContainer.clientWidth
      const targetScrollPosition = currentTimePointer.offsetLeft - containerWidth / 2

      // Scroll to the calculated position
      scrollContainer.style.scrollBehavior = 'smooth'
      scrollContainer.scrollLeft = targetScrollPosition

      // Set back the scroll behavior to auto
      setTimeout(() => {
        scrollContainer.style.scrollBehavior = 'auto'
      }, 1000)
    },
    setCurrentTimePointer() {
      const isToday = moment(this.timeSelected).isSame(new Date(), 'd')

      if (!isToday) {
        this.$refs['current-time'].style.display = 'none'
        return
      }

      const currentTimePointer = this.$refs['current-time']
      const minutesInDay = 24 * 60
      const resourceColWidth = '248px'
      currentTimePointer.style.display = 'block'

      const updatePointer = () => {
        const currentDate = new Date()
        const currentMinutes = currentDate.getHours() * 60 + currentDate.getMinutes()
        currentTimePointer.style.left = `calc(${resourceColWidth} + (${
          currentMinutes / minutesInDay
        }) * (100% - ${resourceColWidth}))`
      }

      updatePointer()

      clearInterval(this.updatePointerInterval)
      this.updatePointerInterval = setInterval(updatePointer, 500)

      // scroll to the current time
      this.scrollToCurrentTime()
    },
    handleClickEvent(eventItem) {
      // TODO: refactor this logic if the event isn't a fleet task
      if (!fleetDestination[eventItem.type]) return

      this.$router.push({ name: fleetDestination[eventItem.type], params: { id: eventItem.id } })
    },
    handleHoverEvent(elementEvent, eventItem) {
      const eventItemElement = elementEvent.target

      // Clone the event item node and set width to auto
      const eventItemElementCloned = eventItemElement.cloneNode(true)
      eventItemElementCloned.style.width = 'auto'
      eventItemElementCloned.style.zIndex = '-1'

      eventItemElement.after(eventItemElementCloned)

      // get the width of ori element and clone element
      const actualWidth = eventItemElement.offsetWidth
      const overflowWidth = eventItemElementCloned.offsetWidth

      // set boolean variable to detect overflow in item
      const isOverflow = overflowWidth > actualWidth

      eventItemElementCloned.remove()

      // return if it's overflow
      if (!isOverflow) return

      // open popover if it's entering the el
      if (elementEvent.type == 'mouseenter') {
        this.$refs[`popover-${eventItem.id}-ref`][0].$emit('open')
      }

      // close popover if it's leaving the el
      if (elementEvent.type == 'mouseleave') {
        this.$refs[`popover-${eventItem.id}-ref`][0].$emit('close')
      }
    },
  },
}
</script>
<style scoped lang="scss">
.wrapper-mc-timeline-day {
  overflow-x: auto;
  overflow-y: auto;
  position: relative;
  max-height: calc(100vh - 350px);

  &::-webkit-scrollbar {
    height: 8px;

    &:vertical {
      display: none;
    }
  }

  &::-webkit-scrollbar-track {
    background-color: $mep-secondary-100;
    margin-left: 248px;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: $mep-primary;
    border-radius: 8px;
  }
}

.mc-timeline-day {
  width: 100%;
  position: relative;
  border: 1px solid black;
  border-collapse: collapse;
  table-layout: fixed;

  &,
  th,
  td {
    border: 1px solid #ebecef;
  }

  td {
    height: 80px;
  }

  th {
    font-weight: 600;
    background-color: #fafcff;
    position: sticky;
    top: 0;
    z-index: 40;

    &::before {
      content: '';
      position: absolute;
      top: -1px;
      left: 0;
      right: 0;
      height: 1px;
      width: 100%;
      background-color: #ebecef;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      top: 100%;
      height: 1px;
      width: 100%;
      background-color: #ebecef;
    }
  }

  th,
  td {
    &:first-child {
      width: 248px;
    }

    &:not(:first-child) {
      width: 120px;
    }
  }

  th,
  .resource {
    padding: 8px;
  }

  th.resource {
    z-index: 50;
    .artificial-border {
      width: 1px;
      position: absolute;
      top: -1px;
      bottom: -1px;
      height: calc(100% + 2px);
      left: -1px;
      background-color: #ebecef;
    }
  }

  td:first-child {
    background-color: white;
  }
}

.resource {
  min-width: 248px;
  position: sticky;
  z-index: 30;
  left: 0;

  &__container {
    text-align: center;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;

    i::before {
      color: $red;
    }
  }

  .__title {
    font-weight: 600;
  }
}

td.resource {
  &::after {
    position: absolute;
    top: 0;
    left: 100%;
    width: 20px;
    background: linear-gradient(90deg, $gray -62.5%, rgba(231, 236, 244, 0) 100%);
    height: 100%;
    content: '';
    pointer-events: none;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -1px;
    bottom: 0;
    width: 1px;
    height: 100%;
    background-color: #ebecef;
  }
}

.time-slot {
  min-width: 120px;
  position: relative;
}

.popover-row {
  display: flex;
  align-items: center;
  gap: 4px;

  :deep(.mc-icon::before) {
    font-size: 16px;
  }
}

.popover-event-item {
  :deep(.popover-header) {
    background-color: #344264;
    color: $white;
    padding: 8px;
  }

  :deep(.popover-body) {
    padding: 8px;
    font-size: 13px;
    color: #01133d;
  }
  .popover-row {
    :deep(div) {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
}

.current-time {
  width: 2px;
  background-color: $red;
  height: 100%;
  position: absolute;
  display: none;
  bottom: 0;
  height: calc(100% - 36px);
  z-index: 20;
}
</style>
